import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import usePartnerPromoOffers from 'luxPlus/hooks/usePartnerPromoOffers'
import LuxPlusLandingNavigationContext from 'luxPlus/contexts/LuxPlusLandingNavigationContext'

const Section = styled.div`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} 0;
  }
`

const OfferCard = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  height: 100%;
  border: none;
  min-width: ${rem(270)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(360)};
  }
`

const OfferImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(180)};
  overflow: hidden;

  /*
   * Logo/Image components (TuroLogo, HelloFreshLogo, Image) define their own
   * intrinsic sizes for use elsewhere (e.g. modals, inline text). To ensure
   * uniform card images here, we override those internal dimensions by
   * stretching all children to fill this fixed-size container.
   */
  > *, picture {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  img, svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

function LuxPlusLandingPartnerOffersSection() {
  const intl = useIntl()
  const { navigateToPartnerOffers } = useContext(LuxPlusLandingNavigationContext)
  const partnerOffers = usePartnerPromoOffers()

  if (!partnerOffers.length) return null

  return (
    <Section>
      <Group direction="vertical" gap={24}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
          <Heading variant="heading3">
            {intl.formatMessage({
              id: 'ah0ULCdyerjaXTJJf3T3',
              defaultMessage: 'Exclusive partner offers',
              description: 'Heading for exclusive partner offers section on LuxPlus landing page',
            })}
          </Heading>
          <TextButton kind="tertiary" size="medium" onClick={navigateToPartnerOffers}>
            {intl.formatMessage({
              id: 'nS6wh6PsYy2FkoCd3jWT',
              defaultMessage: 'View all',
              description: 'Button to view all partner offers on LuxPlus landing page',
            })}
          </TextButton>
        </Group>
        <CardCarousel pageSize={3}>
          {partnerOffers.map((offer) => (
            <OfferCard key={offer.id} borderRadius="L">
              <OfferImageContainer>
                {offer.logo}
              </OfferImageContainer>
              <Group direction="vertical" gap={12}>
                <Heading variant="heading4" lineClamp={2}>{offer.title}</Heading>
                <BodyText variant="small" colour="neutral-two">{offer.description}</BodyText>
                {offer.onViewDetails && <TextLink size="medium" weight="regular" onClick={offer.onViewDetails}>
                  {offer.ctaLabel}
                </TextLink>}
              </Group>
            </OfferCard>
          ))}
        </CardCarousel>
      </Group>
    </Section>
  )
}

export default LuxPlusLandingPartnerOffersSection
